import { useContext, useState } from 'react'
import {
  XCircleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid'
import ButtonEl from '../helpers/ctas/Button'
import themeConfig from '../../../theme.config'
import { MarkupContext } from '../../providers/Markup'

// Icon + colour class per alert variant.
// Keys mirror themeConfig.settings.alerts (error / warning / success /
// notification / attention) — DWR-47 schema.
const VARIANT_CONFIG = {
  error:        { Icon: XCircleIcon,             colorClass: 'bg-alert-error-bg text-alert-error-text' },
  warning:      { Icon: ExclamationCircleIcon,   colorClass: 'bg-alert-warning-bg text-alert-warning-text' },
  success:      { Icon: CheckCircleIcon,         colorClass: 'bg-alert-success-bg text-alert-success-text' },
  notification: { Icon: InformationCircleIcon,   colorClass: 'bg-alert-notification-bg text-alert-notification-text' },
  attention:    { Icon: ExclamationTriangleIcon, colorClass: 'bg-alert-attention-bg text-alert-attention-text' },
}

const createCookieValue = (str) => {
  if (!str?.length) return 'alertcookie'
  const el = document.createElement('div')
  el.innerHTML = str
  return el.innerText.toLowerCase().replace(/\W/g, '').substring(0, 25)
}

const Alert = (props) => {
  const { createMarkup } = useContext(MarkupContext)
  const [active, setActive] = useState(true)

  // Fall back to warning if the saved variant is no longer in the config
  // (e.g. legacy content from before DWR-47).
  const type = props.type?.toLowerCase() || 'warning'
  const alertColors = VARIANT_CONFIG[type] ?? VARIANT_CONFIG.warning

  const cookieID = `_cookie-${type}`
  const cookieValueStr = typeof document !== 'undefined' ? createCookieValue(props.title) : 'alertcookie'

  const hasSetCookie =
    typeof document !== 'undefined' &&
    document.cookie.split(';').some(
      (item) => item.trim().startsWith(cookieID) && item.includes(cookieValueStr)
    )

  const handleClose = () => {
    setActive(false)
    const expires = new Date()
    expires.setDate(expires.getDate() + 14)
    document.cookie = `${cookieID}=${cookieValueStr}; expires=${expires.toUTCString()}; path=/;`
  }

  if (props.dismissible !== false && (hasSetCookie || !active)) return null

  const { Icon, colorClass } = VARIANT_CONFIG[type] ?? VARIANT_CONFIG.warning
  const isUrgent = props.urgent || type === 'error'

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`${colorClass} w-full`}
    >
      <div className="container lg:w-3/5 mx-auto px-4 py-4 flex justify-start items-start gap-4">
        <Icon className={`w-6 h-6 shrink-0 mt-0.5 ${colorClass}`} aria-hidden="true" />
        <div className="grow">
          {props.title && (
            <p className="font-semibold mb-1" data-rte="true" dangerouslySetInnerHTML={createMarkup(props.title)} />
          )}
          {props.description && (
            <p className="text-sm" data-rte="true" dangerouslySetInnerHTML={createMarkup(props.description)} />
          )}
        </div>
        {props.dismissible !== false && (
          <ButtonEl outerClass={`shrink-0 ${colorClass} cursor-pointer`} onClick={handleClose} theme="none">
            <XMarkIcon className={`w-5 h-5 ${colorClass}`} aria-hidden="true" />
            <span className="sr-only">Close this alert</span>
          </ButtonEl>
        )}
      </div>
    </div>
  )
}

export default Alert
